// eslint-disable-next-line no-unused-vars
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { object, func, bool } from 'prop-types';
import { useContextSelector } from 'use-context-selector';
import { useForm } from 'react-hook-form';
import { useParams, useLocation } from 'react-router-dom';

import { WishlistContext } from 'Contexts/WishlistContext';
import EditWishlistModalComponent from 'Components/Wishlists/EditWishlistModal';
import ModalManager from 'Containers/ModalManager/ModalManager';
import useToastNotifications from 'Hooks/useToastNotifications';
import useAnalytics from 'Hooks/useAnalytics';

const defaultValues = {
  name: '',
  isDefault: false,
};

const EditWishlistModal = ({
  wishlist,
  modalOpen,
  onCloseModal
}) => {
  const { wishlists, updateList } = useContextSelector(WishlistContext, data => ({
    updateList: data.updateList,
    wishlists: data.data
  }));
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const { id } = useParams();
  const location = useLocation();
  const toast = useToastNotifications();
  const analytics = useAnalytics();

  // check if the current path matches `/lists/:id`
  const isListPage = location.pathname.startsWith('/lists/');
  const currentListId = isListPage ? id : null;

  const {
    formState: { errors: rhfErrors },
    register,
    reset,
    watch,
    getValues,
    setValue,
    trigger,
  } = useForm({
    defaultValues,
    mode: 'onChange'
  });

  const isDefaultValue = watch('isDefault');

  const required = t('errors.fieldBlank');
  const minLength = value => ({ value, message: t('errors.fieldMinLength', { minLength: value }) });
  const maxLength = value => ({ value, message: t('errors.fieldMaxLength', { maxLength: value }) });

  const validators = {
    name: {
      required,
      minLength: minLength(3),
      maxLength: maxLength(50),
      validate: {
        duplicate: value => {
          if (!value) return true;
          const normalizedValue = value.trim().toLowerCase();
          const duplicateExists = Object.values(wishlists)
            .filter(list => list.id !== currentListId)
            .find(list => list.name.trim().toLowerCase() === normalizedValue);
          if (normalizedValue === 'stock alerts' || duplicateExists) {
            return t('wishlists.errors.sameName');
          }
          return true;
        }
      }
    },
    isDefault: {}
  };

  useEffect(() => {
    if (
      modalOpen
      && wishlist?.id
    ) {
      reset({
        name: wishlist.name,
        isDefault: wishlist.isDefault
      });
    }
  }, [
    modalOpen,
    wishlist?.id
  ]);

  const handleOnClose = () => {
    if (!loading) {
      onCloseModal();
      setLoading(false);
      reset(defaultValues);
    }
  };

  const handleOnSubmit = async e => {
    e.preventDefault();
    analytics.track('submitted_edit_wishlist_modal');

    await trigger();

    const rhfErrorsKeys = Object.keys(rhfErrors);
    if (rhfErrorsKeys.length) {
      return;
    }

    try {
      setLoading(true);
      const formData = getValues();
      await updateList(wishlist.id, formData);

      toast.addNotification({
        type: 'success',
        title: t('wishlist.updated')
      });

      setTimeout(() => {
        onCloseModal();
        reset(defaultValues);
      }, 500);
    } catch (error) {
      toast.addNotification({
        type: 'error',
        title: t('wishlist.updateFailed')
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalManager open={ modalOpen }>
      <EditWishlistModalComponent
        loading={ loading }
        onClose={ handleOnClose }
        onSubmit={ handleOnSubmit }
        validators={ validators }
        register={ register }
        rhfErrors={ rhfErrors }
        setValue={ setValue }
        isDefaultValue={ isDefaultValue }
      />
    </ModalManager>
  );
};

EditWishlistModal.propTypes = {
  modalOpen: bool,
  onCloseModal: func,
  wishlist: object
};

export default EditWishlistModal;
